<template>
  <div class="util">
    <!-- Single venue snapshot — radial gauge -->
    <div v-if="!grouped && single" class="util-single">
      <div class="util-gauge">
        <svg class="util-gauge__svg" viewBox="0 0 180 180" preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id="util-gauge-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stop-color="#ba54f5" />
              <stop offset="100%" stop-color="#e14eca" />
            </linearGradient>
          </defs>
          <circle class="util-gauge__track" cx="90" cy="90" r="72"
                  :stroke-dasharray="trackDash" transform="rotate(135 90 90)" />
          <circle class="util-gauge__prog" cx="90" cy="90" r="72"
                  stroke="url(#util-gauge-grad)" :stroke-dasharray="progDash" transform="rotate(135 90 90)" />
        </svg>
        <div class="util-gauge__center">
          <div class="util-gauge__pct">
            {{ single.utilization == null ? '–' : single.utilization }}<span v-if="single.utilization != null" class="util-gauge__pct-sign">%</span>
          </div>
          <div class="util-gauge__label">utilized</div>
          <div class="util-gauge__ctx">{{ fmt(single.booked_hours) }} / {{ fmt(single.available_hours) }}h</div>
        </div>
      </div>

      <div class="util-legend">
        <div class="util-legend__item">
          <span class="util-legend__dot util-legend__dot--booked"></span>
          <span class="util-legend__val">{{ fmt(single.booked_hours) }}h</span>
          <span class="util-legend__lbl">booked</span>
        </div>
        <div class="util-legend__rule"></div>
        <div class="util-legend__item">
          <span class="util-legend__dot util-legend__dot--idle"></span>
          <span class="util-legend__val">{{ fmt(single.idle_hours) }}h</span>
          <span class="util-legend__lbl">idle</span>
        </div>
      </div>
    </div>

    <!-- Per-resource breakdown -->
    <div v-else-if="grouped" class="util-list">
      <div v-for="(row, idx) in rows" :key="'ug-' + idx" class="util-row">
        <div class="util-row__head">
          <span class="util-row__label" :title="row.label">{{ row.label }}</span>
          <span class="util-row__pct">{{ row.utilization == null ? '–' : row.utilization + '%' }}</span>
        </div>
        <div class="util-row__meter" :title="barTitle(row)">
          <div class="util-row__fill" :style="{ width: fillPct(row) + '%' }"></div>
        </div>
        <div class="util-row__sub">{{ fmt(row.booked_hours) }}h booked · {{ fmt(row.idle_hours) }}h idle</div>
      </div>
      <div v-if="rows.length === 0" class="util-empty">No resources in range</div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Row {
  label?: string
  available_hours: number | null
  booked_hours: number | null
  idle_hours: number | null
  utilization: number | null
}

const props = defineProps<{
  data: Row[]
  grouped?: boolean
}>()

const rows = computed<Row[]>(() => Array.isArray(props.data) ? props.data : [])
const single = computed<Row | null>(() => (rows.value.length > 0 ? rows.value[0] : null))

// 270° ring (gap at the bottom). C = circumference; the visible track is 75% of it.
const R = 72
const C = 2 * Math.PI * R
const ARC = 0.75
const trackDash = `${(C * ARC).toFixed(2)} ${C.toFixed(2)}`
const progDash = computed(() => {
  const u = single.value ? Math.max(0, Math.min(100, Number(single.value.utilization) || 0)) : 0
  return `${(C * ARC * (u / 100)).toFixed(2)} ${C.toFixed(2)}`
})

function fmt(v: number | null | undefined): string {
  if (v === null || v === undefined || isNaN(Number(v))) return '–'
  return Number(v).toFixed(1)
}
function fillPct(row: Row): number {
  const avail = Number(row.available_hours)
  const booked = Number(row.booked_hours)
  if (!avail || avail <= 0 || isNaN(booked)) return 0
  return Math.max(0, Math.min(100, (booked / avail) * 100))
}
function barTitle(row: Row): string {
  return `${fmt(row.booked_hours)}h booked of ${fmt(row.available_hours)}h staffed`
}
</script>

<style scoped>
.util { width: 100%; display: flex; flex-direction: column; }

/* ── Single: radial gauge ─────────────────────────────── */
.util-single { display: flex; flex-direction: column; align-items: center; }
.util-gauge {
  position: relative;
  width: 100%;
  max-width: 220px;
  margin: 4px auto 0;
}
.util-gauge__svg { display: block; width: 100%; height: auto; }
.util-gauge__track {
  fill: none;
  stroke: rgba(130, 130, 145, 0.18);
  stroke-width: 13;
  stroke-linecap: round;
}
.util-gauge__prog {
  fill: none;
  stroke-width: 13;
  stroke-linecap: round;
  filter: drop-shadow(0 0 5px rgba(225, 78, 202, 0.5));
  transition: stroke-dasharray 0.7s cubic-bezier(0.22, 1, 0.36, 1);
}
.util-gauge__center {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  /* nudge up so the open bottom of the ring doesn't look unbalanced */
  padding-bottom: 6px;
}
.util-gauge__pct {
  font-size: 2.5rem;
  font-weight: 800;
  line-height: 1;
  letter-spacing: -0.03em;
  background: linear-gradient(135deg, #ba54f5 0%, #e14eca 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}
.util-gauge__pct-sign { font-size: 1.25rem; font-weight: 700; }
.util-gauge__label {
  margin-top: 3px;
  font-size: 0.66rem;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.4);
}
.util-gauge__ctx {
  margin-top: 7px;
  font-size: 0.74rem;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.55);
}

/* ── Legend ───────────────────────────────────────────── */
.util-legend {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 12px;
}
.util-legend__item { display: flex; align-items: baseline; gap: 6px; }
.util-legend__dot {
  width: 8px; height: 8px; border-radius: 50%;
  align-self: center; flex-shrink: 0;
}
.util-legend__dot--booked { background: #e14eca; box-shadow: 0 0 6px rgba(225, 78, 202, 0.6); }
.util-legend__dot--idle { background: rgba(140, 140, 155, 0.55); }
.util-legend__val { font-size: 0.92rem; font-weight: 700; color: #fff; }
.util-legend__lbl { font-size: 0.72rem; color: rgba(255, 255, 255, 0.45); }
.util-legend__rule { width: 1px; height: 20px; background: rgba(255, 255, 255, 0.12); }

/* ── Grouped list ─────────────────────────────────────── */
.util-list { display: flex; flex-direction: column; gap: 14px; max-height: 360px; overflow-y: auto; padding-right: 2px; }
.util-row__head { display: flex; justify-content: space-between; align-items: baseline; margin-bottom: 6px; }
.util-row__label {
  font-size: 0.88rem; font-weight: 500; color: rgba(255, 255, 255, 0.92);
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 68%;
}
.util-row__pct { font-size: 0.88rem; font-weight: 700; color: #e14eca; }
.util-row__meter {
  position: relative; height: 8px; border-radius: 5px;
  background: rgba(130, 130, 145, 0.18); overflow: hidden;
}
.util-row__fill {
  height: 100%; border-radius: inherit;
  background: linear-gradient(90deg, #ba54f5 0%, #e14eca 100%);
  box-shadow: 0 0 8px rgba(225, 78, 202, 0.5);
  transition: width 0.5s cubic-bezier(0.22, 1, 0.36, 1);
}
.util-row__sub { font-size: 0.72rem; color: rgba(255, 255, 255, 0.45); margin-top: 5px; }
.util-empty { font-size: 0.82rem; color: rgba(255, 255, 255, 0.5); text-align: center; padding: 16px 0; }

/* ── Light mode ───────────────────────────────────────── */
.white-content .util-gauge__track { stroke: rgba(0, 0, 0, 0.1); }
.white-content .util-gauge__label { color: rgba(0, 0, 0, 0.4); }
.white-content .util-gauge__ctx { color: rgba(0, 0, 0, 0.55); }
.white-content .util-legend__val { color: #1d253b; }
.white-content .util-legend__lbl { color: rgba(0, 0, 0, 0.45); }
.white-content .util-legend__rule { background: rgba(0, 0, 0, 0.12); }
.white-content .util-row__label { color: rgba(0, 0, 0, 0.85); }
.white-content .util-row__sub { color: rgba(0, 0, 0, 0.45); }
.white-content .util-row__meter { background: rgba(0, 0, 0, 0.1); }
</style>
